/**
 * Finishd Dashboard
 * User-specific dashboard based on user type
 */

import { Bookmark, CircleCheck, Compass, Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { ContractorHome } from "@/features/dashboard/components/contractor-home";
import { PayoutAccountCard } from "@/features/dashboard/components/payout-account-card";
import { VerificationStatusCard } from "@/features/dashboard/components/verification-status-card";
import { DesignerDashboardRedesign } from "@/features/marketplace/pages/dashboard-redesign";
import {
	browseContractors,
	browsePublicProjects,
	type ContractorProfile,
	type DesignerProfile,
	fetchSavedIdeas,
	fetchToggleSavedIdea,
	getDesignerProfile,
	getFeaturedDesigners,
	getSavedIdeas,
	type PublicProjectSummary,
} from "@/features/marketplace/services/marketplace-api.service";
import {
	getProjects,
	type Project,
} from "@/features/projects/services/projects-api.service";
import { RealtorDashboard } from "@/features/realtor/pages/realtor-dashboard";
import { usePayoutAccount } from "@/features/settings/hooks/use-payout-account";
import { getHomeownerProfile } from "@/features/settings/services/settings-api.service";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { formatPrice } from "@/lib/format";

// ============================================================================
// HOMEOWNER DASHBOARD
// ============================================================================

function HomeownerDashboard() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [projects, setProjects] = useState<Project[]>([]);
	const [publicProjects, setPublicProjects] = useState<PublicProjectSummary[]>(
		[],
	);
	const [featuredDesigners, setFeaturedDesigners] = useState<DesignerProfile[]>(
		[],
	);
	const [featuredContractors, setFeaturedContractors] = useState<
		ContractorProfile[]
	>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [profileComplete, setProfileComplete] = useState(false);
	const [city, setCity] = useState<string | null>(null);
	const [savedIdeas, setSavedIdeas] = useState<string[]>([]);
	const [savedIdeasProjects, setSavedIdeasProjects] = useState<
		PublicProjectSummary[]
	>([]);
	const [error, setError] = useState<string | null>(null);
	// One save toggle at a time. The ref stops a second click at once, before
	// the state change shows the disabled buttons.
	const toggleInFlightRef = useRef(false);
	const [toggleInFlight, setToggleInFlight] = useState(false);

	const loadData = useCallback(async () => {
		setIsLoading(true);
		try {
			setError(null);
			const [
				projectsData,
				profile,
				designers,
				contractors,
				publicProjectData,
				savedIdeasData,
			] = await Promise.all([
				getProjects(),
				getHomeownerProfile(),
				getFeaturedDesigners(),
				browseContractors({ limit: 6 }),
				browsePublicProjects({ limit: 12 }),
				getSavedIdeas(),
			]);
			setProjects(projectsData);
			setProfileComplete(!!profile);
			setCity(profile?.city || null);
			setFeaturedDesigners(designers);
			setFeaturedContractors(contractors.contractors);
			setPublicProjects(publicProjectData.projects);
			setSavedIdeas(savedIdeasData.projectIds);
			setSavedIdeasProjects(savedIdeasData.projects);
		} catch (error) {
			console.error("Failed to load dashboard data:", error);
			setError(t("dashboard.loadError", "Failed to load data"));
		} finally {
			setIsLoading(false);
		}
	}, [t]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-28 w-full rounded-xl" />
				<div>
					<Skeleton className="mb-4 h-6 w-40" />
					<Skeleton className="h-24 w-full rounded-lg" />
				</div>
				<div>
					<Skeleton className="mb-4 h-6 w-48" />
					<div className="grid gap-4 md:grid-cols-2">
						<Skeleton className="h-40 rounded-lg" />
						<Skeleton className="h-40 rounded-lg" />
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-center">
				<p className="text-destructive">{error}</p>
				<Button variant="outline" onClick={loadData} className="mt-4">
					{t("common.tryAgain", "Try Again")}
				</Button>
			</div>
		);
	}

	// Show onboarding prompt if profile not complete
	if (!profileComplete) {
		return (
			<div className="rounded-lg bg-card p-5 sm:p-8 text-center shadow-sm">
				<h2 className="text-xl font-semibold text-foreground">
					{t("dashboard.completeProfile")}
				</h2>
				<p className="mt-2 text-muted-foreground">
					{t("dashboard.setupHomeownerProfile")}
				</p>
				<Button
					onClick={() => navigate("/onboarding/homeowner")}
					variant="primary"
					className="mt-4"
				>
					{t("dashboard.completeProfileBtn")}
				</Button>
			</div>
		);
	}

	const activeProjects = projects.filter(
		(p) => p.status !== "completed" && p.status !== "cancelled",
	);
	const activeProject = activeProjects[0];
	const projectsNearYou = publicProjects.slice(0, 4);
	const budgetFriendlyBuckets = [
		{
			label: "Under ₹2L",
			match: (p: PublicProjectSummary) =>
				(p.budgetMax || p.budgetMin || 0) <= 200000,
		},
		{
			label: "Under ₹5L",
			match: (p: PublicProjectSummary) =>
				(p.budgetMax || p.budgetMin || 0) <= 500000,
		},
		{
			label: "Luxury Spaces",
			match: (p: PublicProjectSummary) =>
				(p.budgetMax || p.budgetMin || 0) > 500000,
		},
	];
	const savedProjectItems = savedIdeasProjects;

	async function onToggleSaveIdea(project: PublicProjectSummary) {
		if (toggleInFlightRef.current) return;
		toggleInFlightRef.current = true;
		setToggleInFlight(true);
		try {
			let result: Awaited<ReturnType<typeof fetchToggleSavedIdea>>;
			try {
				result = await fetchToggleSavedIdea(project.id);
			} catch (error) {
				console.error("Failed to toggle saved idea:", error);
				toast.error(t("inspire.saveFailed"));
				// The toggle can fail after the server changed the list, for example
				// when the answer is lost. Read the list again to show what the
				// server has. If that read also fails, keep what the page shows.
				try {
					const saved = await fetchSavedIdeas();
					setSavedIdeas(saved.projectIds);
					setSavedIdeasProjects(saved.projects);
				} catch (reloadError) {
					console.error("Failed to reload saved ideas:", reloadError);
				}
				return;
			}
			const savedIds = new Set(result.projectIds);
			setSavedIdeas(result.projectIds);
			// Keep only the cards of projects that the server has saved.
			setSavedIdeasProjects((prev) => {
				const kept = prev.filter((item) => savedIds.has(item.id));
				if (!savedIds.has(project.id)) return kept;
				if (kept.some((item) => item.id === project.id)) return kept;
				return [project, ...kept];
			});
		} finally {
			toggleInFlightRef.current = false;
			setToggleInFlight(false);
		}
	}

	return (
		<div className="space-y-8">
			<div className="rounded-xl bg-linear-to-r from-primary/15 via-accent/20 to-primary/5 p-6">
				<div className="flex items-center gap-2 text-primary">
					<Compass className="h-4 w-4" />
					<p className="text-sm font-medium">
						{t("dashboard.explore", "Explore")}
					</p>
				</div>
				<h2 className="mt-2 text-xl font-semibold text-foreground">
					{t(
						"dashboard.discoverTransformations",
						"Discover ideas, professionals, and real projects",
					)}
				</h2>
				<p className="mt-1 text-sm text-muted-foreground">
					{city
						? t(
								"dashboard.exploreCity",
								`Curated inspiration and experts in ${city}.`,
							)
						: t(
								"dashboard.exploreGeneral",
								"Curated inspiration for your next home project.",
							)}
				</p>
			</div>

			<div>
				<h2 className="mb-4 text-lg font-semibold text-foreground">
					Active Project
				</h2>
				{activeProject ? (
					<Card>
						<CardContent className="p-6">
							<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
								<div>
									<p className="text-xl font-semibold text-foreground">
										{activeProject.title}
									</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{activeProject.budgetMin && activeProject.budgetMax
											? `${formatPrice(activeProject.budgetMin)} - ${formatPrice(activeProject.budgetMax)}`
											: t("common.budgetNotSet")}
									</p>
								</div>
								<div className="flex items-center gap-2">
									<StatusBadge status={activeProject.status} />
									<Button
										size="sm"
										onClick={() => navigate(`/projects/${activeProject.id}`)}
									>
										View Project
									</Button>
								</div>
							</div>
						</CardContent>
					</Card>
				) : (
					<Card>
						<CardContent className="p-6">
							<p className="text-sm text-muted-foreground">
								{t(
									"dashboard.noActiveProjectExplore",
									"No active project yet. Start one to track progress and budget in one place.",
								)}
							</p>
							<Button
								className="mt-4"
								onClick={() => navigate("/projects/new")}
								variant="primary"
							>
								<Plus className="mr-2 h-4 w-4" />
								Start New Project
							</Button>
						</CardContent>
					</Card>
				)}
			</div>

			<div>
				<div className="mb-4 flex items-center justify-between">
					<h2 className="text-lg font-semibold text-foreground">
						Trending Transformations
					</h2>
					{publicProjects.length > 0 && (
						<Link
							to="/public-projects"
							className="text-sm text-primary hover:text-primary/80"
						>
							{t("common.viewAll")}
						</Link>
					)}
				</div>

				<div className="grid gap-4 md:grid-cols-2">
					{publicProjects.slice(0, 4).map((project) => {
						const saved = savedIdeas.includes(project.id);
						return (
							<div
								key={project.id}
								className="rounded-lg border bg-card p-4 shadow-sm"
							>
								<button
									type="button"
									onClick={() => navigate(`/public-projects/${project.id}`)}
									className="w-full text-left"
								>
									<div className="grid gap-2 sm:grid-cols-2">
										{project.previewImages?.[0] ? (
											<img
												src={project.previewImages[0]}
												alt={`${project.title} before`}
												className="h-28 w-full rounded-md object-cover"
											/>
										) : (
											<div className="rounded-md bg-muted p-6 text-center text-xs font-medium text-muted-foreground">
												Before
											</div>
										)}
										{project.previewImages?.[1] ? (
											<img
												src={project.previewImages[1]}
												alt={`${project.title} after`}
												className="h-28 w-full rounded-md object-cover"
											/>
										) : (
											<div className="rounded-md bg-primary/10 p-6 text-center text-xs font-medium text-primary">
												After
											</div>
										)}
									</div>
									<p className="mt-3 font-semibold text-foreground">
										{project.title}
									</p>
									<p className="mt-1 text-xs text-muted-foreground">
										{project.scope === "full_home" ? "Home" : "Partial"} •{" "}
										{project.homeownerName}
									</p>
									{project.previewImages.length > 2 && (
										<p className="mt-1 text-xs text-primary">
											+{Math.floor(project.previewImages.length / 2) - 1} more
											before/after sets
										</p>
									)}
								</button>
								<Button
									variant={saved ? "secondary" : "outline"}
									size="sm"
									className="mt-3 w-full"
									disabled={toggleInFlight}
									onClick={() => onToggleSaveIdea(project)}
								>
									<Bookmark className="mr-2 h-4 w-4" />
									{saved ? "Saved" : "Save Idea"}
								</Button>
							</div>
						);
					})}
				</div>
			</div>

			<div>
				<div className="mb-4 flex items-center justify-between">
					<h2 className="text-lg font-semibold text-foreground">
						Discover Professionals
					</h2>
					<Link
						to="/designers"
						className="text-sm text-primary hover:text-primary/80"
					>
						{t("common.viewAll")}
					</Link>
				</div>
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{featuredDesigners.slice(0, 3).map((designer) => (
						<button
							type="button"
							key={designer.id}
							onClick={() => navigate(`/designers/${designer.id}`)}
							className="cursor-pointer rounded-lg border bg-card p-4 text-left shadow-sm transition-shadow hover:shadow-md"
						>
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-semibold">
									{designer.name.charAt(0)}
								</div>
								<div>
									<p className="font-semibold text-foreground">
										{designer.name}
									</p>
									<p className="text-xs text-muted-foreground">Designer</p>
								</div>
							</div>
						</button>
					))}
					{featuredContractors.slice(0, 3).map((contractor) => (
						<button
							type="button"
							key={contractor.id}
							onClick={() => navigate(`/contractors/${contractor.id}`)}
							className="cursor-pointer rounded-lg border bg-card p-4 text-left shadow-sm transition-shadow hover:shadow-md"
						>
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-semibold">
									{contractor.name.charAt(0)}
								</div>
								<div>
									<p className="font-semibold text-foreground">
										{contractor.name}
									</p>
									<p className="text-xs text-muted-foreground">Contractor</p>
								</div>
							</div>
						</button>
					))}
				</div>
			</div>

			<div>
				<h2 className="mb-4 text-lg font-semibold text-foreground">
					Projects Near You
				</h2>
				<div className="grid gap-4 md:grid-cols-2">
					{projectsNearYou.map((project) => (
						<button
							type="button"
							key={project.id}
							onClick={() => navigate(`/public-projects/${project.id}`)}
							className="cursor-pointer rounded-lg border bg-card p-4 text-left shadow-sm transition-shadow hover:shadow-md"
						>
							<p className="font-semibold text-foreground">{project.title}</p>
							<p className="mt-1 text-xs text-muted-foreground">
								{city || "Your city"} • {project.homeownerName}
							</p>
						</button>
					))}
				</div>
			</div>

			<div>
				<h2 className="mb-4 text-lg font-semibold text-foreground">
					Budget Friendly Ideas
				</h2>
				<div className="grid gap-4 md:grid-cols-3">
					{budgetFriendlyBuckets.map((bucket) => {
						const project = publicProjects.find(bucket.match);
						return (
							<div
								key={bucket.label}
								className="rounded-lg border bg-card p-4 shadow-sm"
							>
								<p className="text-xs font-semibold uppercase text-primary">
									{bucket.label}
								</p>
								{project ? (
									<button
										type="button"
										className="mt-2 text-left"
										onClick={() => navigate(`/public-projects/${project.id}`)}
									>
										<p className="font-semibold text-foreground">
											{project.title}
										</p>
										<p className="text-xs text-muted-foreground">
											{project.budgetMin && project.budgetMax
												? `${formatPrice(project.budgetMin)} - ${formatPrice(project.budgetMax)}`
												: t("common.budgetNotSet")}
										</p>
									</button>
								) : (
									<p className="mt-2 text-sm text-muted-foreground">
										No ideas available in this budget yet.
									</p>
								)}
							</div>
						);
					})}
				</div>
			</div>

			<div>
				<h2 className="mb-4 text-lg font-semibold text-foreground">
					Saved Ideas
				</h2>
				{savedProjectItems.length === 0 ? (
					<Card>
						<CardContent className="p-6">
							<p className="text-sm text-muted-foreground">
								Save project ideas to build your inspiration board.
							</p>
						</CardContent>
					</Card>
				) : (
					<div className="grid gap-4 md:grid-cols-2">
						{savedProjectItems.map((project) => (
							<button
								type="button"
								key={project.id}
								onClick={() => navigate(`/public-projects/${project.id}`)}
								className="cursor-pointer rounded-lg border bg-card p-4 text-left shadow-sm transition-shadow hover:shadow-md"
							>
								<p className="font-semibold text-foreground">{project.title}</p>
								<p className="text-xs text-muted-foreground">
									{project.homeownerName}
								</p>
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

// ============================================================================
// DESIGNER DASHBOARD
// ============================================================================

function DesignerDashboard() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [profileComplete, setProfileComplete] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [reviewStatus, setReviewStatus] = useState<
		"pending" | "approved" | "rejected" | undefined
	>(undefined);
	const [reviewNotes, setReviewNotes] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const payoutQuery = usePayoutAccount(true);
	const payoutAccount = payoutQuery.data ?? null;
	const payoutLoading = payoutQuery.isLoading;

	useEffect(() => {
		// `silent` refetches (on window focus) update status without flashing the
		// skeleton, so an admin approval flips "under review" -> verified the next
		// time the designer returns to the tab, without a hard reload (#207.3).
		const loadProfile = async (opts?: { silent?: boolean }) => {
			if (!opts?.silent) {
				setIsLoading(true);
			}
			try {
				const profile = await getDesignerProfile();
				setProfileComplete(!!profile);
				setIsVerified(profile?.isVerified || false);
				setReviewStatus(profile?.reviewStatus);
				setReviewNotes(profile?.reviewNotes ?? null);
			} catch (error) {
				console.error("Failed to load profile:", error);
			} finally {
				if (!opts?.silent) {
					setIsLoading(false);
				}
			}
		};
		loadProfile();
		const onFocus = () => loadProfile({ silent: true });
		window.addEventListener("focus", onFocus);
		return () => window.removeEventListener("focus", onFocus);
	}, []);

	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-24 w-full rounded-lg" />
				<Skeleton className="h-24 w-full rounded-lg" />
			</div>
		);
	}

	if (!profileComplete) {
		return (
			<FloatPanel
				elevation={2}
				style={{
					maxWidth: 500,
					margin: "40px auto",
					textAlign: "center",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					padding: "40px 24px",
				}}
			>
				<div
					style={{
						width: 64,
						height: 64,
						borderRadius: "50%",
						background: "var(--color-dashboard-warm-200)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						marginBottom: 20,
						border: "1px solid var(--color-dashboard-border)",
					}}
				>
					<Compass className="h-8 w-8 text-[var(--color-dashboard-accent)]" />
				</div>
				<h2
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 600,
						fontSize: 20,
						color: "var(--color-dashboard-text)",
						margin: 0,
					}}
				>
					{t("dashboard.completeProfile")}
				</h2>
				<p
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 14,
						color: "var(--color-dashboard-muted)",
						marginTop: 8,
						marginBottom: 24,
						maxWidth: 360,
						lineHeight: "1.5",
					}}
				>
					{t("dashboard.setupDesignerProfile")}
				</p>
				<StudioButton
					onClick={() => navigate("/onboarding/designer")}
					variant="primary"
					size="md"
				>
					{t("dashboard.completeProfileBtn")}
				</StudioButton>
			</FloatPanel>
		);
	}

	if (!isVerified) {
		return (
			<div className="space-y-6">
				<PayoutAccountCard
					payoutAccount={payoutAccount}
					isLoading={payoutLoading}
				/>
				<VerificationStatusCard
					userType="designer"
					reviewStatus={reviewStatus}
					reviewNotes={reviewNotes}
				/>

				<Card>
					<CardContent className="p-6">
						<h3 className="font-semibold text-foreground">
							{t("dashboard.whileYouWait")}
						</h3>
						<ul className="mt-4 space-y-2 text-sm text-muted-foreground">
							<li className="flex items-center gap-2">
								<CircleCheck className="h-4 w-4 text-primary" />
								{t("dashboard.addPortfolioImages")}
							</li>
							<li className="flex items-center gap-2">
								<CircleCheck className="h-4 w-4 text-primary" />
								{t("dashboard.completeBioDetails")}
							</li>
							<li className="flex items-center gap-2">
								<CircleCheck className="h-4 w-4 text-primary" />
								{t("dashboard.addMoreServiceCities")}
							</li>
						</ul>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Use redesigned dashboard for verified designers
	return (
		<div className="space-y-6">
			<PayoutAccountCard
				payoutAccount={payoutAccount}
				isLoading={payoutLoading}
			/>
			<DesignerDashboardRedesign />
		</div>
	);
}

// ============================================================================
// MAIN DASHBOARD
// ============================================================================

export function DashboardPage() {
	const { t } = useTranslation();
	const { user } = useAuthStore();
	const navigate = useNavigate();

	return (
		<div className="space-y-6">
			{user?.userType === "homeowner" && <HomeownerDashboard />}
			{user?.userType === "designer" && (
				<div className="px-4 pt-4 md:px-0 md:pt-0">
					<DesignerDashboard />
				</div>
			)}
			{user?.userType === "contractor" && (
				<div className="px-4 pt-4 md:px-0 md:pt-0">
					<ContractorHome />
				</div>
			)}
			{user?.userType === "realtor" && <RealtorDashboard />}
			{!user?.userType && (
				<div className="rounded-lg bg-card p-5 sm:p-8 text-center shadow-sm">
					<h2 className="text-xl font-semibold text-foreground">
						{t("dashboard.selectRole")}
					</h2>
					<p className="mt-2 text-muted-foreground">
						{t("dashboard.chooseHowToUse")}
					</p>
					<Button
						onClick={() => navigate("/onboarding")}
						variant="primary"
						className="mt-4"
					>
						{t("common.getStarted")}
					</Button>
				</div>
			)}
		</div>
	);
}
